// Dispute panel — the customer/recipient side of "verified or refunded". Shows
// the open/resolved dispute, or a "Report a problem" form on a delivered/
// confirmed order. Opening one holds the merchant's payout until an admin
// resolves it.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, AlertTriangle, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from './primitives';
import { Button } from '../stripe-ui/Button';
import { disputes, type DisputeReason, type DisputeStatus } from '../../lib/api';

const REASONS: { v: DisputeReason; label: string }[] = [
  { v: 'not_received', label: 'Never arrived' },
  { v: 'wrong_items', label: 'Wrong items' },
  { v: 'damaged', label: 'Damaged or spoiled' },
  { v: 'quantity_short', label: 'Quantity short' },
  { v: 'quality', label: 'Poor quality' },
  { v: 'other', label: 'Something else' },
];

const STATUS_UI: Record<DisputeStatus, { label: string; cls: string }> = {
  open:               { label: 'Under review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  investigating:      { label: 'Under review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  refund_issued:      { label: 'Refund issued', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  merchant_charged:   { label: 'Resolved in your favor', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  resolved_no_action: { label: 'Closed — no action', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
};

export function DisputePanel({ requestId, status, canOpen }: { requestId: number; status: string; canOpen: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['dispute', requestId], queryFn: () => disputes.forRequest(requestId) });
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<DisputeReason>('not_received');
  const [details, setDetails] = useState('');

  const file = useMutation({
    mutationFn: () => disputes.open(requestId, { reason, details: details.trim() || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispute', requestId] });
      qc.invalidateQueries({ queryKey: ['me', 'request', requestId] });
      setOpen(false); setDetails('');
      toast.success('Reported — VoyeLakay is reviewing it');
    },
    onError: (e: Error) => toast.error(e.message || 'Could not file the report'),
  });

  if (q.isLoading) return null;
  // The API returns {} (not null) when there's no dispute — only treat it as
  // one if it actually has an id.
  const dispute = q.data && q.data.id ? q.data : null;

  // Existing dispute → show its state.
  if (dispute) {
    const ui = STATUS_UI[dispute.status] ?? { label: dispute.status, cls: 'bg-slate-50 text-slate-600 border-slate-200' };
    return (
      <Card className={`border ${ui.cls}`}>
        <div className="flex items-start gap-2.5">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[13px] font-semibold inline-flex items-center gap-2">Dispute · {ui.label}</p>
            <p className="text-[12px] opacity-90 mt-0.5">{REASONS.find((x) => x.v === dispute.reason)?.label ?? dispute.reason}{dispute.details ? ` — “${dispute.details}”` : ''}</p>
            {dispute.resolution && <p className="text-[12px] mt-1.5"><b>Resolution:</b> {dispute.resolution}</p>}
            {(dispute.status === 'open' || dispute.status === 'investigating') && <p className="text-[11px] opacity-80 mt-1.5">The merchant's payout is on hold while we review.</p>}
          </div>
        </div>
      </Card>
    );
  }

  // Only delivered/confirmed orders can be disputed.
  if (!canOpen || !['delivered', 'confirmed'].includes(status)) return null;

  return (
    <Card className="border-slate-200">
      {!open ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-[12.5px] text-slate-600 inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-500" /> Didn't get what you expected? You're covered — <b className="font-semibold">verified or refunded</b>.
          </p>
          <button onClick={() => setOpen(true)} className="text-[12.5px] font-semibold text-rose-600 hover:underline shrink-0">Report a problem</button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[13px] font-semibold text-slate-900">Report a problem</p>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">What went wrong?</span>
            <select value={reason} onChange={(e) => setReason(e.target.value as DisputeReason)} className="mt-1 block w-full px-3 py-2 text-[13px] border border-slate-200 rounded-md bg-white">
              {REASONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Details (optional)</span>
            <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3} placeholder="Tell us what happened…" className="mt-1 block w-full px-3 py-2 text-[13px] border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
          </label>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => file.mutate()} loading={file.isPending} leadingIcon={<Send className="w-3.5 h-3.5" />}>Submit report</Button>
            <button onClick={() => setOpen(false)} className="text-[12.5px] text-slate-500 hover:text-slate-900">Cancel</button>
          </div>
          <p className="text-[11px] text-slate-400 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 hidden" />This holds the merchant's payout while VoyeLakay reviews it.</p>
        </div>
      )}
    </Card>
  );
}
